import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Shield, CheckCircle, AlertOctagon, Link as LinkIcon, Copy, Send, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Per-booking Safety panel.
 *
 * Opens from the Bookings tab for confirmed mobile bookings. Bundles
 * three actions:
 *   - I'm safe check-ins (confirm pending/missed arrival or departure)
 *   - Panic alert (one tap, blasts SMS to emergency contacts + admin)
 *   - Live share link (copyable URL plus optional contact SMS fan-out)
 */
export default function SafetyBookingDialog({ booking, open, onOpenChange }) {
  const [checkins, setCheckins] = useState([]);
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(false);
  const [panicNotes, setPanicNotes] = useState('');
  const [acting, setActing] = useState(false);
  // Share link privacy mode: 'full' shows location + check-ins,
  // 'quiet' redacts both so loved ones see only the timing.
  const [shareMode, setShareMode] = useState('full');

  const refresh = useCallback(async () => {
    if (!booking?.booking_id) return;
    setLoading(true);
    try {
      const res = await api.get('/safety/checkins/me');
      const mine = (res.data || []).filter(c => c.booking_id === booking.booking_id);
      setCheckins(mine);
    } catch {
      setCheckins([]);
    } finally {
      setLoading(false);
    }
  }, [booking?.booking_id]);

  useEffect(() => {
    if (open) {
      refresh();
      setShare(null);
      setPanicNotes('');
    }
  }, [open, refresh]);

  const handleConfirm = async (checkin_id) => {
    setActing(true);
    try {
      await api.post(`/safety/checkins/${checkin_id}/confirm`);
      toast.success("Marked as I'm safe");
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not confirm check-in');
    } finally { setActing(false); }
  };

  const handlePanic = async () => {
    if (!window.confirm('Send a panic alert? This will SMS every emergency contact and flag the booking for the Zuriya safety team.')) return;
    setActing(true);
    try {
      const res = await api.post('/safety/panic', {
        booking_id: booking.booking_id,
        notes: panicNotes || undefined,
      });
      toast.success(`Panic alert sent. ${res.data?.contacts_notified || 0} contacts notified.`);
      setPanicNotes('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send panic alert');
    } finally { setActing(false); }
  };

  const handleShare = async (notify_contacts = false) => {
    setActing(true);
    try {
      const res = await api.post(`/safety/bookings/${booking.booking_id}/share`, { notify_contacts, mode: shareMode });
      setShare(res.data);
      if (notify_contacts) {
        toast.success(`Live link sent to ${res.data.contacts_notified || 0} contacts`);
      } else {
        toast.success(`${shareMode === 'quiet' ? 'Quiet' : 'Live'} share link created`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create share link');
    } finally { setActing(false); }
  };

  const handleCopy = async () => {
    if (!share?.url) return;
    try {
      await navigator.clipboard.writeText(share.url);
      toast.success('Link copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  if (!booking) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="safety-booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#D4AF37]" />Safety
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 mt-2">
          <div className="text-xs text-stone-500">
            {booking.service_name} with {booking.client_name} on {booking.date} at {booking.time}
          </div>

          {/* Check-ins */}
          <section data-testid="safety-checkins-section">
            <p className="text-sm font-medium text-[#1A1A1A] mb-2">Safety check-ins</p>
            {loading && <p className="text-xs text-stone-400">Loading&hellip;</p>}
            {!loading && checkins.length === 0 && (
              <p className="text-xs text-stone-500">No check-ins scheduled for this booking yet. They&rsquo;re created automatically once the appointment is confirmed.</p>
            )}
            <ul className="space-y-2">
              {checkins.map(c => {
                const isPending = c.status === 'pending' || c.status === 'missed';
                return (
                  <li
                    key={c.checkin_id}
                    className="flex items-center justify-between p-2 bg-[#F9F6F0] rounded-lg"
                    data-testid={`checkin-row-${c.checkin_id}`}
                  >
                    <div className="text-xs">
                      <p className="font-medium text-[#1A1A1A] capitalize">{c.type} check-in</p>
                      <p className="text-stone-500">
                        {c.status === 'confirmed'
                          ? `Confirmed ${c.confirmed_at?.slice(11, 16) || ''}`
                          : c.status === 'escalated'
                            ? 'Escalated to contacts'
                            : c.status === 'missed'
                              ? 'Missed, awaiting your tap'
                              : `Due ${c.scheduled_for?.slice(11, 16) || ''}`}
                      </p>
                    </div>
                    {isPending ? (
                      <Button
                        size="sm"
                        onClick={() => handleConfirm(c.checkin_id)}
                        disabled={acting}
                        className="btn-primary gap-1"
                        data-testid={`confirm-checkin-${c.checkin_id}`}
                      >
                        <CheckCircle className="h-3.5 w-3.5" />I&rsquo;m safe
                      </Button>
                    ) : (
                      <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                        {c.status}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>

          {/* Live share link */}
          <section data-testid="safety-share-section">
            <p className="text-sm font-medium text-[#1A1A1A] mb-2">Live appointment status</p>
            <p className="text-xs text-stone-500 mb-2">
              Generate a private URL friends or family can open to see where you are, when you&rsquo;re expected to finish, and whether you&rsquo;ve checked in. The location shown is only your postcode area, never a full address.
            </p>
            {!share ? (
              <>
                <div className="mb-2 flex flex-wrap gap-2" data-testid="share-mode-toggle">
                  <button
                    type="button"
                    onClick={() => setShareMode('full')}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${shareMode === 'full' ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white text-stone-600 border-[#E6E4DD] hover:border-stone-400'}`}
                    data-testid="share-mode-full"
                  >
                    Full status
                  </button>
                  <button
                    type="button"
                    onClick={() => setShareMode('quiet')}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${shareMode === 'quiet' ? 'bg-[#1A1A1A] text-white border-[#1A1A1A]' : 'bg-white text-stone-600 border-[#E6E4DD] hover:border-stone-400'}`}
                    data-testid="share-mode-quiet"
                  >
                    Quiet (hide location)
                  </button>
                </div>
                <p className="text-[11px] text-stone-400 mb-2" data-testid="share-mode-hint">
                  {shareMode === 'quiet'
                    ? 'Quiet links hide the postcode area and individual check-in steps. Loved ones see your appointment timing and can still tap I\u2019ve made contact.'
                    : 'Full links show the postcode area and each check-in as you confirm it.'}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleShare(false)}
                    disabled={acting}
                    className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-amber-50 gap-1"
                    data-testid="create-share-link-btn"
                  >
                    <LinkIcon className="h-3.5 w-3.5" />Generate link
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleShare(true)}
                    disabled={acting}
                    className="btn-primary gap-1"
                    data-testid="share-link-notify-btn"
                  >
                    <Send className="h-3.5 w-3.5" />Generate &amp; text my contacts
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 p-2 bg-white border border-[#E6E4DD] rounded-lg" data-testid="share-link-result">
                <input
                  type="text"
                  readOnly
                  value={share.url}
                  className="flex-1 text-xs text-stone-600 bg-transparent outline-none truncate"
                  data-testid="share-link-url"
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="shrink-0 text-stone-500 hover:text-[#D4AF37] p-1"
                  aria-label="Copy link"
                  data-testid="copy-share-link-btn"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setShare(null)}
                  className="shrink-0 text-stone-400 hover:text-stone-600 p-1"
                  aria-label="Dismiss"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
          </section>

          {/* Panic alert */}
          <section className="border border-red-100 rounded-lg p-3 bg-red-50/50" data-testid="safety-panic-section">
            <p className="text-sm font-semibold text-red-700 mb-1 flex items-center gap-2">
              <AlertOctagon className="h-4 w-4" />Panic alert
            </p>
            <p className="text-xs text-stone-600 mb-2">
              For genuine emergencies only. Texts every emergency contact with your last known location and notifies the Zuriya safety team. If you are in immediate danger, call 999 first.
            </p>
            <Textarea
              value={panicNotes}
              onChange={(e) => setPanicNotes(e.target.value)}
              placeholder="Optional note: anything we should pass to your contacts"
              className="input-zuriya min-h-[60px] text-xs"
              data-testid="panic-notes-input"
            />
            <Button
              size="sm"
              onClick={handlePanic}
              disabled={acting}
              className="mt-2 bg-red-600 hover:bg-red-700 text-white gap-1 w-full"
              data-testid="trigger-panic-btn"
            >
              <AlertOctagon className="h-3.5 w-3.5" />Send panic alert
            </Button>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}
